import { makeStyles } from '@mui/styles';
import Accordion from 'apollo-react/components/Accordion';
import AccordionSummary from 'apollo-react/components/AccordionSummary';
import IconButton from 'apollo-react/components/IconButton';
import Tab from 'apollo-react/components/Tab';
import Tabs from 'apollo-react/components/Tabs';
import Tooltip from 'apollo-react/components/Tooltip';
import Typography from 'apollo-react/components/Typography';
import Info from 'apollo-react-icons/Info';
import React, { useEffect } from 'react';

import ControlPropertiesValidationUpdate from './ControlPropertiesValidationUpdate';
import FormModelDataSourcesTable from './FormModelDataSourcesTable';
import PropertyComponent from './PropertyComponent';
import { useFormSelector } from '../../../custom-hooks';
import { useTranslation } from '../../../custom-hooks/useTranslation';
import { getUITA } from '../../../utils/commonUtils';
import {
  AUTOGENERATED_ZOOM_INFO,
  CONTROL_TYPES_ON_CHANGE_AND_DATA_SOURCE_TABS,
  CONTROL_TYPES_PROPERTY_TABS_ONLY,
} from '../../constants/editor.constants';
import { PROPERTIES } from '../../constants/editor.properties.constants';
import {
  FORM_MODEL_ATTRIBUTES,
  SS_CONTROL_TYPE,
  ZOOM_SOURCE_TYPE,
} from '../../constants/form-manager.constants';
import {
  getAddedElementFormModelById,
  getSelectedControlId,
} from '../../selectors/editor.selectors';
import {
  getFormKey,
  getIsParentAdvancedTextBox,
  getPropByModel,
} from '../../selectors/form-manager.selectors';

const useStyles = makeStyles({
  root: {
    '& div[class*="MuiAccordionSummary-root"]': {
      marginLeft: '-2rem',
    },
  },
  controlTypeWrapper: {
    display: 'flex',
    alignItems: 'center',
  },
  controlType: {
    padding: 5,
    color: '#666',
  },
  controlTitle: {
    color: '#666',
  },
});

const ZoomInfo = ({ title }) => {
  return (
    <Tooltip extraLabels={[{ title }]} placement="right" variant="light">
      <IconButton size="small">
        <Info color="primary" />
      </IconButton>
    </Tooltip>
  );
};

const ControlProperties = () => {
  const classes = useStyles();
  const selectedControl = useFormSelector(getSelectedControlId);
  const zoomSource = useFormSelector(getPropByModel, {
    id: selectedControl,
    prop: FORM_MODEL_ATTRIBUTES.ZOOM_SOURCE,
  });
  const controlType = useFormSelector(getPropByModel, {
    id: selectedControl,
    prop: FORM_MODEL_ATTRIBUTES.CONTROL_TYPE,
  });
  const isParentAdvancedTextBox = useFormSelector(getIsParentAdvancedTextBox, {
    id: selectedControl,
    prop: FORM_MODEL_ATTRIBUTES.CONTROL_TYPE,
  });
  const entityName = useFormSelector(getPropByModel, {
    id: selectedControl,
    prop: FORM_MODEL_ATTRIBUTES.ENTITY_NAME,
  });
  const fieldName = useFormSelector(getPropByModel, {
    id: selectedControl,
    prop: FORM_MODEL_ATTRIBUTES.FIELD_NAME,
  });

  const addedElementFormModel = useFormSelector(getAddedElementFormModelById, {
    controlId: selectedControl,
  });
  const { controlId, saved } = addedElementFormModel || [];
  const formKey = useFormSelector(getFormKey) || '';

  const isNewElement = controlId === selectedControl;
  const attributes = PROPERTIES[controlType] || [];
  const translateString = useTranslation();

  const renderTitle = () => {
    const entityNamePath = entityName.split('-');
    const translatedEntityName = translateString(entityNamePath[entityNamePath.length - 1]);

    const tooltipLabels = [
      { title: '', subtitle: fieldName ? `${entityName}.${fieldName}` : `${entityName}` },
    ];
    return (
      <>
        <h5 className={classes.controlTitle}>
          {fieldName ? 'Entity / Field Name:' : 'Entity Name:'}
        </h5>
        <Tooltip extraLabels={tooltipLabels} placement="bottom-start" variant="light">
          <h3 ui-ta={getUITA('CtrlPropPanel', entityName, fieldName)}>
            {fieldName
              ? `${translatedEntityName} / ${translateString(fieldName)}`
              : `${translatedEntityName}`}
          </h3>
        </Tooltip>
      </>
    );
  };

  const showPropertytabOnly = CONTROL_TYPES_PROPERTY_TABS_ONLY.includes(controlType ?? '');
  const showDataSourceTab =
    CONTROL_TYPES_ON_CHANGE_AND_DATA_SOURCE_TABS.includes(controlType) &&
    selectedControl === 'formBox';

  const hidePropertyTab =
    formKey.toLowerCase().includes('_detail') && controlType === SS_CONTROL_TYPE.FORM;

  const tabTitle = CONTROL_TYPES_ON_CHANGE_AND_DATA_SOURCE_TABS.includes(controlType ?? '')
    ? 'Rules'
    : 'On Change';

  const isAdvancedTextBox = controlType === SS_CONTROL_TYPE.ADVANCED_TEXT_BOX;

  const [value, setValue] = React.useState(0);

  const handleChangeTab = (event, value) => {
    setValue(value);
  };

  useEffect(() => {
    if (hidePropertyTab) {
      setValue(1);
    } else {
      setValue(0);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedControl, hidePropertyTab]);

  const filterPropertyAttribute = (item) => {
    if (['On Change'].includes(item.label)) {
      return false;
    }

    if (controlType === SS_CONTROL_TYPE.FORM && ['Help Link'].includes(item.label)) {
      return true;
    } else if (controlType === SS_CONTROL_TYPE.FORM) {
      return false;
    }

    return true;
  };

  return (
    <div className={classes.root}>
      {!controlType ? <h3>{'Control Properties'}</h3> : entityName ? renderTitle() : null}
      <div className={classes.controlTypeWrapper}>
        {controlType && <h5 className={classes.controlType}>{controlType}</h5>}
        {controlType === SS_CONTROL_TYPE.ZOOM && zoomSource === ZOOM_SOURCE_TYPE.ENTITY && (
          <ZoomInfo title={AUTOGENERATED_ZOOM_INFO} />
        )}
      </div>
      <div>
        <Tabs
          value={value}
          onChange={handleChangeTab}
          size="small"
          truncate={process.env.NODE_ENV !== 'test'}
        >
          <Tab label="Properties" style={{ display: hidePropertyTab ? 'none' : 'block' }} />
          <Tab
            label={tabTitle}
            ui-ta={getUITA('PropPanel', tabTitle, 'Tab')}
            style={{ display: showPropertytabOnly ? 'none' : 'block' }}
          />
          <Tab label="Data Sources" style={{ display: showDataSourceTab ? 'block' : 'none' }} />
        </Tabs>
        <div style={{ padding: 24 }}>
          {value === 0 && (
            <div>
              {attributes.length ? (
                attributes.filter(filterPropertyAttribute).map(({ label, properties }) => (
                  <div key={label}>
                    <Accordion defaultExpanded>
                      <AccordionSummary>
                        <Typography>{label}</Typography>
                      </AccordionSummary>
                      {properties
                        .filter((item) =>
                          isNewElement &&
                          !saved &&
                          !(controlType === SS_CONTROL_TYPE.TEXT_VIEW && isParentAdvancedTextBox)
                            ? true
                            : !['Control Type'].includes(item.label)
                        )
                        .map((props) => (
                          <PropertyComponent
                            key={props.label}
                            controlId={selectedControl}
                            {...props}
                          />
                        ))}
                    </Accordion>
                  </div>
                ))
              ) : (
                <Typography>
                  {controlType && isAdvancedTextBox
                    ? 'Advanced Text Box is a group of controls and can be moved together'
                    : controlType
                    ? `There are no property definitions for ${controlType}`
                    : 'Select a control'}
                </Typography>
              )}
            </div>
          )}
          {value === 1 && (
            <div>
              {attributes.length ? (
                attributes
                  .filter((item) => ['On Change'].includes(item.label))
                  .map(({ label, properties }) => (
                    <ControlPropertiesValidationUpdate
                      key={label}
                      label={label}
                      controlId={selectedControl}
                      properties={properties}
                    />
                  ))
              ) : (
                <Typography>
                  {controlType
                    ? `There is no property definition for ${controlType}`
                    : 'Select a control'}
                </Typography>
              )}
            </div>
          )}
          {value === 2 && (
            <div>
              {attributes.length ? (
                attributes
                  .filter((item) => ['Data Sources'].includes(item.label))
                  .map(({ label, properties }) => (
                    <FormModelDataSourcesTable
                      key={label}
                      label={label}
                      controlId={selectedControl}
                      properties={properties}
                    />
                  ))
              ) : (
                <Typography>
                  {controlType
                    ? `There is no property definition for ${controlType}`
                    : 'Select a control'}
                </Typography>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ControlProperties;
